import { Mail, MapPin, Phone, Facebook, Youtube, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { CONTACT } from "../components/Footer";
import SEO from "../components/SEO";

export default function Contact({ onStartTax, onBookAppointment }) {
  return (
    <div data-testid="contact-page" className="pt-16">
      <SEO path="/contact"
        title="Contact Us | Marizema Tax Service — Tampa, FL"
        description="Contact Marizema Tax Service LLC — call (813) 728-0702 or email marizematax@gmail.com. Serving Tampa, Florida." />
      <section className="py-16 sm:py-24 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-3">Contact Us</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
            Let's talk about your taxes
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Reach out any time — we reply to every message, usually within one business day.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-5">
              <a data-testid="contact-phone-link" href={CONTACT.phoneTel}
                className="flex items-start gap-4 bg-white border border-slate-200 hover:border-[#C9A227]/60 rounded-2xl p-6 transition-colors group">
                <div className="w-11 h-11 rounded-xl bg-[#0B2545] flex items-center justify-center shrink-0">
                  <Phone size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-serif-display text-lg font-semibold text-[#0B2545] group-hover:text-[#86680A] transition-colors">Call us</h2>
                  <p className="text-sm text-[#1F2933]/80 mt-1">{CONTACT.phoneDisplay}</p>
                  <p className="text-xs text-[#1F2933]/75 mt-1">Tap to call — we answer during business hours.</p>
                </div>
              </a>

              <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`}
                className="flex items-start gap-4 bg-white border border-slate-200 hover:border-[#C9A227]/60 rounded-2xl p-6 transition-colors group">
                <div className="w-11 h-11 rounded-xl bg-[#0B2545] flex items-center justify-center shrink-0">
                  <Mail size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-serif-display text-lg font-semibold text-[#0B2545] group-hover:text-[#86680A] transition-colors">Email us</h2>
                  <p className="text-sm text-[#1F2933]/80 mt-1">{CONTACT.email}</p>
                  <p className="text-xs text-[#1F2933]/75 mt-1">Send documents or questions any time.</p>
                </div>
              </a>

              <div className="flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-[#0B2545] flex items-center justify-center shrink-0">
                  <MapPin size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-serif-display text-lg font-semibold text-[#0B2545]">Visit us</h2>
                  <p className="text-sm text-[#1F2933]/80 mt-1">{CONTACT.location}</p>
                  <p className="text-xs text-[#1F2933]/75 mt-1">Serving the greater Tampa Bay communities.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-[#0B2545] flex items-center justify-center shrink-0">
                  <Clock size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-serif-display text-lg font-semibold text-[#0B2545]">Hours</h2>
                  <p className="text-sm text-[#1F2933]/80 mt-1">Monday – Saturday, by appointment</p>
                  <p className="text-xs text-[#1F2933]/75 mt-1">Morning, midday, afternoon, and evening slots available.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="font-serif-display text-lg font-semibold text-[#0B2545] mb-4">Connect with us</h2>
                <div className="flex flex-wrap gap-3">
                  <a data-testid="contact-social-facebook" href={CONTACT.facebook} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B2545]/5 border border-[#0B2545]/20 text-[#0B2545] text-sm font-semibold hover:bg-[#0B2545]/10 transition-colors">
                    <Facebook size={16} /> Facebook
                  </a>
                  <a data-testid="contact-social-youtube" href={CONTACT.youtube} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B2545]/5 border border-[#0B2545]/20 text-[#0B2545] text-sm font-semibold hover:bg-[#0B2545]/10 transition-colors">
                    <Youtube size={16} /> YouTube
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="space-y-5">
              <div data-testid="contact-map" className="rounded-2xl overflow-hidden border border-slate-200 h-64 lg:h-72">
                <iframe title="Marizema Tax Service — Tampa, Florida"
                  src="https://www.google.com/maps?q=Tampa%2C%20Florida&output=embed"
                  className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <div className="bg-[#0B2545] border border-[#C9A227]/30 rounded-2xl p-7">
                <h2 className="font-serif-display text-xl font-semibold text-white">Prefer to get started right away?</h2>
                <p className="mt-2 text-sm text-slate-300">Start your tax return online in minutes, or request an appointment and we'll confirm by email within 1 business day.</p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <button data-testid="contact-cta-start-tax" onClick={onStartTax}
                    className="flex-1 px-5 py-3 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
                    Start Tax Return
                  </button>
                  <button data-testid="contact-cta-book-appointment" onClick={onBookAppointment}
                    className="flex-1 px-5 py-3 rounded-full border border-[#C9A227]/70 text-[#D4AF37] text-sm font-bold hover:bg-[#C9A227]/10 transition-colors">
                    Book Appointment
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
